<template>
  <div>
    <NavigationBreadcrumb :items="breadcrumbItems"></NavigationBreadcrumb>
    <h3 class="mt-3">
      {{ entityNamePlural }}
      <AlternativeNameButton
        :table_name="'mesh.classroomtype'"
        :allows_crud="allowsCrud"
      ></AlternativeNameButton>
    </h3>
    <div style="display: flex">
      <div class="col" style="text-align: left">
        <ButtonMedium
          v-if="allowsCrud"
          v-can="'mesh.add_classroomtype'"
          :tooltip_text="
            $t('reusable.common.addTooltip', { entity: entityName })
          "
          :text_button="$t('reusable.common.add')"
          :click_button="openCreate"
          :icon="'plus'"
          :variant="'primary'"
        ></ButtonMedium>
      </div>
    </div>
    <!-- Mismo aspecto que GenericBTable (contenedor, buscador y tabla), sin Bootstrap-Vue -->
    <div class="table-container mb-3">
      <div class="search-container noprint">
        <div class="input-group input-group-sm search">
          <div class="input-group-prepend">
            <span class="input-group-text">
              <!-- Ícono "search" de Bootstrap Icons, el mismo de GenericBTable -->
              <svg width="1em" height="1em" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
            </span>
          </div>
          <input
            v-model="input_search"
            type="search"
            class="form-control search-input"
            :placeholder="$t('reusable.common.searchPlaceholder')"
          />
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-sm classroom-types-table">
          <thead class="text-center">
            <tr>
              <th v-if="canReorder" class="noprint" style="width: 1%"></th>
              <th>{{ entityNamePlural }}</th>
              <th>{{ $t("reusable.common.description") }}</th>
              <th v-if="allowsCrud" class="noprint" style="width: 1%"></th>
            </tr>
          </thead>
          <draggable
            v-if="visibleClassroomTypes.length > 0"
            :value="classroomTypes"
            :disabled="!canDrag"
            tag="tbody"
            handle=".handle"
            @input="saveOrder"
          >
            <tr
              v-for="classroom_type in visibleClassroomTypes"
              :key="classroom_type.id"
            >
              <td
                v-if="canReorder"
                class="text-center noprint"
                :class="{ 'handle grabbable': canDrag }"
                :title="$t('reusable.common.order')"
              >
                &#8597;
              </td>
              <td class="text-left">{{ classroom_type.name }}</td>
              <td class="text-left description-cell">
                {{ classroom_type.description }}
              </td>
              <td v-if="allowsCrud" class="noprint">
                <div class="d-flex">
                  <ButtonSmall
                    v-can="'mesh.change_classroomtype'"
                    :tooltip_text="
                      $t('reusable.common.editTooltip', { entity: entityName })
                    "
                    :click_button="() => openEdit(classroom_type)"
                    :icon="'square'"
                  ></ButtonSmall>
                  <ButtonSmall
                    v-can="'mesh.delete_classroomtype'"
                    :tooltip_text="
                      $t('reusable.common.deleteTooltip', {
                        entity: entityName,
                      })
                    "
                    :click_button="() => askForDelete(classroom_type.id)"
                    :custom_class="`ml-2`"
                    :icon="'trash'"
                  ></ButtonSmall>
                </div>
              </td>
            </tr>
          </draggable>
          <tbody v-else>
            <tr>
              <td :colspan="columnCount" class="text-center">
                {{
                  $t("reusable.common.noDataContextToShow", {
                    entity: entityNamePlural,
                  })
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <BsModal
      v-model="show_form"
      :title="
        $t(
          editing_classroom_type
            ? 'reusable.common.editModalTitle'
            : 'reusable.common.createModalTitle',
          { entity: entityName }
        )
      "
      size="lg"
    >
      <ClassroomTypeForm
        v-if="show_form"
        :key="editing_classroom_type ? editing_classroom_type.id : 'new'"
        :classroom_type="editing_classroom_type"
        :next_order="nextOrder"
        @created="show_form = false"
        @updated="show_form = false"
      ></ClassroomTypeForm>
    </BsModal>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Catálogo de Tipos de sala de la institución ($usesAcademicLoad). Se asignan por División
// de tiempo en la Carga Académica, en la Asignatura y como valor por defecto de la División.
// El orden es el de las listas desplegables y se cambia arrastrando las filas.
// Legacy (Bootstrap-Vue): NavigationBreadcrumb, AlternativeNameButton y los botones
// compartidos de la app. vuedraggable no depende de Bootstrap-Vue.
import { computed, getCurrentInstance, ref } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import draggable from "vuedraggable";
import BsModal from "@/components/reusable/bs/BsModal";
import ButtonMedium from "@/components/reusable/Buttons/Medium/ButtonMedium";
import ButtonSmall from "@/components/reusable/Buttons/Small/ButtonSmall";
import NavigationBreadcrumb from "@/components/reusable/NavigationBreadcrumb";
import AlternativeNameButton from "@/components/reusable/AlternativeNameButton";
import ClassroomTypeForm from "./ClassroomTypeForm";

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;

const entityName = computed(() =>
  proxy.$getVisibleNames("mesh.classroomtype", false, "Tipo de sala")
);
const entityNamePlural = computed(() =>
  proxy.$getVisibleNames("mesh.classroomtype", true, "Tipos de sala")
);
const breadcrumbItems = computed(() => [
  { text: proxy.$t("reusable.breadcrumb.home"), to: "/home" },
  { text: entityNamePlural.value },
]);

const allowsCrud = computed(() =>
  proxy.$hasPermissions("mesh.add_classroomtype")
);
const canReorder = computed(
  () => allowsCrud.value && proxy.$hasPermissions("mesh.change_classroomtype")
);
const columnCount = computed(
  () => 2 + (canReorder.value ? 1 : 0) + (allowsCrud.value ? 1 : 0)
);

const classroomTypes = computed(() => store.getters[names.CLASSROOM_TYPES]);
const input_search = ref("");
const visibleClassroomTypes = computed(() =>
  classroomTypes.value.filter(
    (x) =>
      proxy.$filtered(x.name, input_search.value) ||
      proxy.$filtered(x.description, input_search.value)
  )
);
// Con un filtro activo se ve solo parte de la lista: arrastrar reordenaría mal el resto
const canDrag = computed(() => canReorder.value && !input_search.value);
// Los nuevos quedan al final de la lista
const nextOrder = computed(
  () => Math.max(0, ...classroomTypes.value.map((x) => x.order)) + 1
);

// Orden 1..n según la posición tras arrastrar: solo los que cambiaron de orden.
function reorderedClassroomTypes(ordered_classroom_types) {
  return ordered_classroom_types
    .map((x, index) => ({ ...x, order: index + 1 }))
    .filter((x, index) => ordered_classroom_types[index].order != x.order);
}

// Se actualiza el store al tiro para que la fila no salte de vuelta mientras se guarda.
function saveOrder(ordered_classroom_types) {
  reorderedClassroomTypes(ordered_classroom_types).forEach((classroom_type) => {
    store.commit(names.MUTATE_CLASSROOM_TYPE, classroom_type);
    store
      .dispatch(names.PATCH_CLASSROOM_TYPE, {
        classroom_type_id: classroom_type.id,
        item: { order: classroom_type.order },
      })
      // Si falla, se recarga para no dejar en pantalla un orden que no se guardó
      .catch(() => store.dispatch(names.FETCH_CLASSROOM_TYPES, true));
  });
}

const show_form = ref(false);
const editing_classroom_type = ref(null);

function openCreate() {
  editing_classroom_type.value = null;
  show_form.value = true;
}

function openEdit(classroom_type) {
  editing_classroom_type.value = classroom_type;
  show_form.value = true;
}

function askForDelete(classroom_type_id) {
  proxy
    .$swal({
      title: proxy.$t("reusable.common.deleteConfirmTitle", {
        entity: entityName.value,
      }),
      text: proxy.$t("reusable.common.deleteConfirmText"),
      type: "warning",
      showCancelButton: true,
    })
    .then((result) => {
      if (!result.value) return;
      store
        .dispatch(names.DELETE_CLASSROOM_TYPE, classroom_type_id)
        .then(() => {
          toast(entityName.value + " " + proxy.$t("reusable.common.deleted"));
        });
    });
}

store.dispatch(names.FETCH_CLASSROOM_TYPES, true);
</script>

<style scoped>
/* Copia del estilo de GenericBTable (tablas del dashboard) */
.table-container {
  margin: 1rem 1rem 0rem 1rem;
}
.search-container {
  display: flex;
  margin-bottom: 1.5em;
}
.search {
  flex: 1 1 auto;
}
.search .input-group-text {
  background: var(--kl-surface);
  border: 1px solid var(--kl-input-border);
  border-right: none;
  border-radius: var(--kl-input-radius) 0 0 var(--kl-input-radius);
  color: var(--kl-text-muted);
}
.search-input {
  height: var(--kl-input-height) !important;
  border: 1px solid var(--kl-input-border);
  border-left: none;
  border-radius: 0 var(--kl-input-radius) var(--kl-input-radius) 0 !important;
  font-size: var(--kl-input-fs);
  color: var(--kl-text-body);
}
.search-input:focus {
  border-color: var(--kl-input-border-focus);
  box-shadow: var(--kl-shadow-focus);
}
.table-responsive {
  border-radius: var(--kl-table-radius);
  border: 1px solid var(--kl-table-border);
}
.classroom-types-table {
  margin-bottom: 0;
}
.classroom-types-table th {
  background: var(--kl-table-header-bg) !important;
  color: var(--kl-table-header-text) !important;
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-table-header-fs);
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
  padding: 11px 12px !important;
  border: none !important;
  vertical-align: middle;
  white-space: normal;
}
.classroom-types-table tbody td {
  color: var(--kl-table-text);
  font-size: var(--kl-table-fs);
  padding: 11px 12px !important;
  border: none !important;
  border-bottom: 1px solid var(--kl-border-soft) !important;
  vertical-align: middle;
  background-color: var(--kl-table-row-bg);
}
.classroom-types-table tbody tr:nth-child(even) > td {
  background-color: var(--kl-table-row-alt-bg);
}
.classroom-types-table tbody tr:hover > td {
  background-color: var(--kl-table-row-hover-bg);
}
.description-cell {
  white-space: pre-line;
}
.grabbable {
  cursor: grab;
}
</style>
